import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { SETUP_QUESTIONS, SETUP_SCREENS, SetupScreenId } from '../../../config/setupQuestions';
import { ScheduleEntry } from '../../../types';
import { getAnswer, isAnswered, setupProgress } from '../../../utils/setupAnswers';
import { nextDateForScreen } from '../../../utils/setupNextDates';
import { getTodayString } from '../../../utils/dateShortcuts';
import QuestionEditor from './QuestionEditor';

const GOLD_SCREENS: SetupScreenId[] = ['shabbos', 'yomTov', 'roshHashana', 'yomKippur'];

export function useNextDateLabel() {
  const { t, i18n } = useTranslation();
  return (screen: SetupScreenId) => {
    const date = nextDateForScreen(screen, getTodayString());
    if (!date) return t('setup.ui.notInYear');
    const label = new Intl.DateTimeFormat(i18n.language === 'he' ? 'he' : 'en', {
      weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
    }).format(new Date(`${date}T12:00:00`));
    return t('setup.ui.next', { date: label });
  };
}

interface YearSetupWizardProps {
  schedules: ScheduleEntry[];
  onSave: (next: ScheduleEntry[]) => Promise<void>;
  onClose: () => void;
  /** Open on this screen (from the review list or the checklist). */
  startScreen?: SetupScreenId;
}

/**
 * A · Guided set-up: one screen per kind of day, in a fixed order, then a check
 * page. Saves when moving between screens, so the admin can stop any time.
 */
export default function YearSetupWizard({ schedules, onSave, onClose, startScreen }: YearSetupWizardProps) {
  const { t } = useTranslation();
  const nextDateLabel = useNextDateLabel();
  const [index, setIndex] = useState(startScreen ? SETUP_SCREENS.indexOf(startScreen) : 0);
  const [draft, setDraft] = useState<ScheduleEntry[]>(schedules);
  const [saving, setSaving] = useState(false);
  const savedRef = useRef<ScheduleEntry[]>(schedules);
  const bodyRef = useRef<HTMLDivElement>(null);
  const total = SETUP_SCREENS.length + 1;
  const inReview = index >= SETUP_SCREENS.length;
  const screen = inReview ? undefined : SETUP_SCREENS[index];

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: 0 });
  }, [index]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') void go(-2);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  });

  /** Save if anything changed, then move (-2 = close). */
  const go = async (target: number) => {
    if (draft !== savedRef.current) {
      setSaving(true);
      try {
        await onSave(draft);
        savedRef.current = draft;
      } finally {
        setSaving(false);
      }
    }
    if (target === -2) onClose();
    else setIndex(target);
  };

  const questions = screen ? SETUP_QUESTIONS.filter((q) => q.screen === screen) : [];
  const leftHere = questions.filter((q) => !isAnswered(getAnswer(draft, q))).length;
  const progress = setupProgress(draft);
  const gold = screen && GOLD_SCREENS.includes(screen);

  return (
    <div className="fixed inset-0 z-50 bg-fog flex flex-col" role="dialog" aria-modal="true" aria-label={t('setup.ui.setupTitle')}>
      <header className={`${gold ? 'bg-gold-600' : 'bg-weekday-600'} text-white px-4 pt-3 pb-3 space-y-2`}>
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="text-xs">{t('setup.ui.setupTitle')}</p>
            <p className="font-semibold">
              {inReview ? t('setup.ui.reviewTitle') : t('setup.ui.stepOf', { n: index + 1, total })}
            </p>
          </div>
          <button type="button" onClick={() => void go(-2)} aria-label={t('setup.ui.close')} className="p-2 rounded-full hover:bg-white/15">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="h-1.5 rounded-full bg-white/30 overflow-hidden">
          <div className="h-full bg-white rounded-full transition-all motion-reduce:transition-none" style={{ width: `${Math.round(((index + 1) / total) * 100)}%` }} />
        </div>
      </header>

      <div ref={bodyRef} className="flex-1 overflow-y-auto">
        <div className="max-w-xl mx-auto px-4 py-4 space-y-3">
          {screen && (
            <>
              <div className="space-y-1">
                <h2 className="text-xl font-semibold text-ink">{t(`setup.screens.${screen}.title`)}</h2>
                <p className="text-sm font-medium text-ink">{nextDateLabel(screen)}</p>
                <p className="text-sm text-ash">{t(`setup.screens.${screen}.intro`)}</p>
              </div>
              {questions.map((q) => (
                <QuestionEditor key={q.id} q={q} schedules={draft} onChange={setDraft} />
              ))}
            </>
          )}

          {inReview && (
            <div className="space-y-3">
              <h2 className="text-xl font-semibold text-ink">
                {progress.answered === progress.total ? t('setup.ui.allSetTitle') : t('setup.ui.missingCount', { count: progress.total - progress.answered })}
              </h2>
              <p className="text-sm text-ash">
                {progress.answered === progress.total ? t('setup.ui.allSetBody') : t('setup.ui.missingBody')}
              </p>
              <ul className="rounded-[16px] border border-ink/10 bg-paper divide-y divide-ink/10">
                {SETUP_SCREENS.map((s, i) => {
                  const missing = progress.missingByScreen[s] ?? 0;
                  return (
                    <li key={s}>
                      <button
                        type="button"
                        onClick={() => setIndex(i)}
                        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-start text-sm hover:bg-fog"
                      >
                        <span className="text-ink">{t(`setup.screens.${s}.title`)}</span>
                        {missing > 0 ? (
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gold-50 text-gold-700">
                            {t('setup.ui.missingCount', { count: missing })}
                          </span>
                        ) : (
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-weekday-50 text-weekday-700">
                            {t('setup.ui.statusDone')}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      </div>

      <footer className="bg-paper border-t border-ink/10 px-4 py-3" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
        <div className="max-w-xl mx-auto flex gap-2">
          <button
            type="button"
            disabled={index === 0 || saving}
            onClick={() => void go(index - 1)}
            className="px-4 py-2 rounded-[10px] border border-ink/20 text-sm font-medium text-ink disabled:opacity-40"
          >
            {t('setup.ui.back')}
          </button>
          {inReview ? (
            <button
              type="button"
              disabled={saving || progress.answered < progress.total}
              onClick={() => void go(-2)}
              className="flex-1 px-4 py-2 rounded-[10px] bg-ink text-paper text-sm font-medium disabled:opacity-50"
            >
              {saving ? t('setup.ui.saving') : progress.answered < progress.total ? t('setup.ui.finishBlocked') : t('setup.ui.finish')}
            </button>
          ) : (
            <button
              type="button"
              disabled={saving}
              onClick={() => void go(index + 1)}
              className="flex-1 px-4 py-2 rounded-[10px] bg-ink text-paper text-sm font-medium disabled:opacity-50"
            >
              {saving ? t('setup.ui.saving') : leftHere ? t('setup.ui.nextLater', { count: leftHere }) : t('setup.ui.saveNext')}
            </button>
          )}
        </div>
      </footer>
    </div>
  );
}
